import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationsApi } from '../services/notifications-api';
import { api } from '@/shared/services/api';

export function useNotifications(params?: Record<string, string>) {
  return useQuery({
    queryKey: ['notifications', params],
    queryFn: () => notificationsApi.list(params),
  });
}

/**
 * Hook unic pentru badge counts (bell + admin support FAB).
 * Inlocuieste useUnreadCount + useAdminUnreadConversationCount cu un singur
 * round-trip la /notifications/badge-counts. Backend agrega counts server-side.
 *
 * Returns: { data: { unreadTotal, supportConversations } | undefined, ... }
 */
export function useBadgeCounts() {
  return useQuery({
    queryKey: ['notifications', 'badge-counts'],
    queryFn: () =>
      api.get<{ unreadTotal: number; supportConversations: number }>('/notifications/badge-counts'),
    refetchInterval: 15000,
    refetchIntervalInBackground: false,
    staleTime: 10000,
  });
}

export function useMarkAsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notificationsApi.markAsRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkAllAsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => notificationsApi.markAllAsRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: ['notification-preferences'],
    queryFn: () => notificationsApi.getPreferences(),
  });
}

export function useUpdateNotificationPreference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ type, data }: { type: string; data: { email?: boolean; inApp?: boolean } }) =>
      notificationsApi.updatePreference(type, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notification-preferences'] });
    },
  });
}
